import React, { useState } from 'react';
import { X, Plus, Trash2, ExternalLink, RefreshCw } from 'lucide-react';

interface CompetitorLinksModalProps {
  product: any;
  onClose: () => void;
  onRefresh: () => void;
}

export const CompetitorLinksModal: React.FC<CompetitorLinksModalProps> = ({ product, onClose, onRefresh }) => {
  const [newUrl, setNewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [scrapingId, setScrapingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const token = localStorage.getItem('tt_token') || localStorage.getItem('token') || localStorage.getItem('tt_auth_token');
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  const handleAddLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUrl) return;
    setLoading(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/pricing/mapping`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ productId: product.id, url: newUrl })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao adicionar link');
      
      setNewUrl('');
      onRefresh();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLink = async (mappingId: string) => {
    if (!window.confirm('Tem certeza que deseja remover este link?')) return;
    try {
      const res = await fetch(`${API_URL}/api/pricing/mapping/${mappingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao deletar');
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  const handleScrape = async (mappingId: string) => {
    setScrapingId(mappingId);
    try {
      const res = await fetch(`${API_URL}/api/pricing/scrape/${mappingId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let data;
      const text = await res.text();
      try {
        data = JSON.parse(text);
      } catch (e) {
        alert('Erro inesperado (Timeout ou Servidor Fora). Código: ' + res.status + ' Resposta: ' + text.substring(0, 100));
        return;
      }

      if (!res.ok) {
        alert(data.error || 'Erro ao fazer scrape');
      } else {
        onRefresh();
      }
    } catch (err: any) {
      console.error(err);
      alert('Erro na requisição: ' + err.message);
    } finally {
      setScrapingId(null);
    }
  };

  const handleScrapeAll = async () => {
    for (const mapping of product.mappings) {
      await handleScrape(mapping.id);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }}>
      <div style={{
        background: 'var(--bg-secondary)', width: '90%', maxWidth: 700, borderRadius: 16, border: '1px solid var(--border-color)',
        display: 'flex', flexDirection: 'column', maxHeight: '90vh'
      }}>
        <div style={{ padding: 20, borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: 18, marginBottom: 4 }}>Concorrentes: {product.productName}</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>SKU: {product.sku} | Nosso Preço: R$ {product.techTennisPrice.toFixed(2)}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
            <X size={24} />
          </button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          <form onSubmit={handleAddLink} style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
            <input 
              type="url"
              placeholder="Cole o link do produto no concorrente (ex: https://mercadolivre.com/...)"
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              style={{
                flex: 1, padding: '12px 16px', borderRadius: 8, border: '1px solid var(--border-color)',
                background: 'var(--bg-primary)', color: 'white', fontSize: 14
              }}
              required
            />
            <button 
              type="submit" 
              disabled={loading || product.mappings.length >= 10}
              style={{
                background: 'var(--primary-color)', color: 'white', border: 'none', padding: '0 20px',
                borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600
              }}
            >
              <Plus size={18} /> {loading ? 'Adicionando...' : 'Adicionar'}
            </button>
          </form>
          {error && <p style={{ color: '#ef4444', fontSize: 13, marginTop: -15, marginBottom: 15 }}>{error}</p>}
          {product.mappings.length >= 10 && <p style={{ color: '#f59e0b', fontSize: 13, marginTop: -15, marginBottom: 15 }}>Limite de 10 links atingido.</p>}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h4 style={{ color: 'var(--text-secondary)' }}>Links Cadastrados ({product.mappings.length}/10)</h4>
            {product.mappings.length > 0 && (
              <button onClick={handleScrapeAll} style={{ background: 'transparent', border: '1px solid #8b5cf6', color: '#8b5cf6', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                Scrapear Todos
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {product.mappings.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.02)', borderRadius: 8, border: '1px dashed var(--border-color)' }}>
                Nenhum link de concorrente cadastrado para este produto.
              </div>
            ) : (
              product.mappings.map((m: any) => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, background: 'var(--bg-primary)', borderRadius: 8, border: '1px solid var(--border-color)' }}>
                  <div style={{ flex: 1, overflow: 'hidden', paddingRight: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <strong style={{ fontSize: 15, textTransform: 'capitalize' }}>{m.competitorName}</strong>
                      <a href={m.competitorUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}>
                        <ExternalLink size={14} />
                      </a>
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {m.competitorUrl}
                    </p>
                  </div>
                  
                  <div style={{ width: 120, textAlign: 'right', paddingRight: 16 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: m.competitorPrice > 0 ? 'white' : 'var(--text-secondary)' }}>
                      {m.competitorPrice > 0 ? `R$ ${m.competitorPrice.toFixed(2)}` : 'S/ Preço'}
                    </div>
                    {m.lastUpdated && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Atualizado {new Date(m.lastUpdated).toLocaleDateString()}</div>}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button 
                      onClick={() => handleScrape(m.id)}
                      disabled={scrapingId === m.id}
                      style={{ background: '#2563eb', color: 'white', border: 'none', padding: 8, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      title="Buscar Preço"
                    >
                      <RefreshCw size={16} className={scrapingId === m.id ? 'spin' : ''} />
                    </button>
                    <button 
                      onClick={() => handleDeleteLink(m.id)}
                      style={{ background: '#ef4444', color: 'white', border: 'none', padding: 8, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      title="Excluir"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};
